import { Suspense, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import type { GetServerSidePropsContext } from 'next';
import { Tabs } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import AddressSelector from '~/components/AddressSelector';
import LoadingSpinnerCenter from '~/components/LoadingSpinnerCenter';
import { TrackPage } from '~/components/TrackEvent';
import { getSnowdonJwt } from '~/domains/auth/utils';
import { MobileAppContextProvider } from '~/hooks/mobileApp';
import AppMarketHomeLink from '~/layouts/AppMarketHomeLink';
import BaseLayout from '~/layouts/BaseLayout';
import { prefetchReactQueryData } from '~/libs/reactQuery';
import type { NextPageWithLayout } from '~/types';
import { initFontScale } from '~/utils/fontScale';

import CartContainer from './components/CartContainer';
import CartFrequentlyOrderedContainer from './components/CartFrequentlyOrderedContainer';

const CART_TAB_TITLES = ['일반상품', '자주구매'];

type TabTitleType = (typeof CART_TAB_TITLES)[number];

const CartPage: NextPageWithLayout = () => {
  const queryClient = useQueryClient();
  const currentTabTitles = CART_TAB_TITLES;
  const [activeTab, setActiveTab] = useState<TabTitleType>(currentTabTitles[0]);

  useEffect(() => {
    trackEvent('clicked tab in 마켓/장바구니', {
      name: activeTab,
    });
  }, [activeTab]);

  const onActiveTabChange = (tab: string) => {
    queryClient.removeQueries(['/api/market-place/v3/shopping-cart-items']);
    setActiveTab(tab);
  };

  return (
    <>
      <TrackPage page="캐시노트마켓/cart" />
      <Tabs
        titles={currentTabTitles}
        variant="navigation-header-tabs"
        defaultActiveKey={activeTab}
        onActiveTabChange={onActiveTabChange}
        className="sticky top-[60px] z-10 bg-screen-01"
      />
      <div className="border-t">
        <AddressSelector />
      </div>

      <Suspense
        fallback={
          <div className="h-screen">
            <LoadingSpinnerCenter immediate loading verticalCenter size={64} />
          </div>
        }
      >
        {activeTab === '일반상품' && <CartContainer />}
        {activeTab === '자주구매' && <CartFrequentlyOrderedContainer />}
      </Suspense>
    </>
  );
};

CartPage.Layout = function Layout({ children }: { children: React.ReactNode }) {
  initFontScale();

  return (
    <MobileAppContextProvider LinkComponent={AppMarketHomeLink}>
      <BaseLayout title="장바구니">{children}</BaseLayout>
    </MobileAppContextProvider>
  );
};

export default CartPage;

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const token = getSnowdonJwt(context) || null;

  if (!token) {
    return {
      props: {},
    };
  }

  const { dehydratedState } = await prefetchReactQueryData({
    queries: [
      [
        '/api/market-place/v3/shopping-cart-items',
        {
          cartType: 'SHOPPING_CART',
        },
      ],
    ],
    fetchOptions: {
      headers: {
        'X-Client-Auth-Token': token,
      },
    },
  });

  return {
    props: {
      dehydratedState,
    },
  };
};
